import { useQuery } from "@tanstack/react-query";
import { httpService } from "../../services/httpService";
import { useAuth } from "../helper/useAuth";
import dayjs from "dayjs";
import { API_CONFIG } from "../../shared/constants";
import { IAppOrdersData, IAppSummaryData } from "../../features/Orders/interface";

interface IAppOrdersDetailParams {
  slug: string;
  startDate?: Date;
  endDate?: Date;
  page?: number;
  per_page?: number;
  search?: string;
  social_account_id?: number;
  country?: string;
  status?: string;
  enabled?: boolean;
}

export const useAppOrdersDetail = ({
  slug,
  startDate,
  endDate,
  page = 1,
  per_page = 15,
  search,
  social_account_id,
  country,
  status,
  enabled,
}: IAppOrdersDetailParams) => {
  const { selectedIntegration } = useAuth();

  const fromDate = startDate ? dayjs(startDate).format("YYYY-MM-DD") : undefined;
  const toDate = endDate ? dayjs(endDate).format("YYYY-MM-DD") : undefined;

  return useQuery({
    queryKey: [
      "app-orders-detail",
      slug,
      selectedIntegration?.value,
      fromDate,
      toDate,
      page,
      per_page,
      search,
      social_account_id,
      country,
      status,
    ],
    queryFn: async () => {
      const params: Record<string, string | number> = {
        page,
        per_page,
        app_slug: slug,
      };

      if (selectedIntegration?.value) params.tracking_id = selectedIntegration.value;
      if (fromDate) params.start_date = fromDate;
      if (toDate) params.end_date = toDate;
      if (search) params.search = search;
      if (social_account_id && !Number.isNaN(social_account_id)) params.social_account_id = social_account_id;
      if (country) params.country = country;
      if (status) params.status = status;

      const response = await httpService.get<IAppSummaryData>(
        API_CONFIG.path.googleIapAppOrdersConsolidated,
        params
      );
      
      return {
        ...response,
        results: response.apps?.[0]?.orders || [],
        total: response.apps?.[0]?.metrics?.total_orders || 0,
      } as IAppOrdersData;
    },
    enabled: enabled !== undefined ? enabled : (!!selectedIntegration?.value && !!slug),
    staleTime: 5 * 60 * 1000,
  });
};
